import { SearchQuery, parse } from "../searchquery/searchQuery";
import { buildPrefixMatcher } from "../util/string";


/** Function that returns an ItemFilter for an array of items and an array of columns to search by default. */
export type ItemFilterBuilder = <TItem>(items: TItem[], filterColumns: (string|number)[]) => ItemFilter;

/** Function that invokes a callback with a set of items matching the given filter value. */
export type ItemFilter = (filterValue: string, callback: ItemFilterCallback) => void;

/** Callback function that accepts an array of items matching a filter expression and an optional error. */
export type ItemFilterCallback = <TItem>(items: TItem[], error?: any) => void;

/**
 * Default item filter, using 'buildPrefixMatch' as the property/default value matcher.
 */
function defaultItemFilter<TViewModel>(query: SearchQuery, item: TViewModel, filterColumns: string[]): boolean {
    switch (query.type) {
        case "and":
            return defaultItemFilter(query.children[0], item, filterColumns)
                && defaultItemFilter(query.children[1], item, filterColumns);

        case "not":
            return !defaultItemFilter(query.children[0], item, filterColumns);

        case "or":
            return defaultItemFilter(query.children[0], item, filterColumns)
                || defaultItemFilter(query.children[1], item, filterColumns);

        case "property":
            const propertyMatcher = buildPrefixMatcher(query.phrase);
            return propertyMatcher.test(getValue(item, query.property));

        default:
            const defaultMatcher = buildPrefixMatcher(query.phrase);
            for (const column of filterColumns) {
                if (defaultMatcher.test(getValue(item, column))) {
                    return true;
                }
            }
            return false;
    }
}

/**
 * Unwraps the display value of an item at the given key.
 */
function getValue<TViewModel>(item: TViewModel, key: string): string {
    const o = (item as any)[key];
    if (o && typeof o.peekValue === "function") {
        return o.peekValue();
    }
    if (o && typeof o.peek === "function") {
        return o.peek();
    }
    return o;
}

/**
 * Returns an item filter that uses 'searchquery' semantics with prefix-matching.
 *
 * @param items
 *     Items to filter.
 * @param filterColumns
 *     Columns (keys/indices) that will be searched by default.
 * @returns
 *     ItemFilter function that invokes the supplied callback with the matching items.
 */
export function defaultItemFilterBuilder<TItem>(items: TItem[], filterColumns: string[]): ItemFilter {
    return (filterValue, callback) => {
        const query = parse(filterValue);
        try {
            const matchingItems = items.filter(item => defaultItemFilter(query, item, filterColumns));
            callback(matchingItems);
        } catch (e) {
            callback(e);
        }
    };
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/ko-grid/itemFilter.ts